/**
 * Runtime application configuration
 * Loaded from /config.json at application startup
 */
export interface AppConfig {
  /**
   * Absolute Portals API base URL, no trailing slash. Deployed portals use their own origin's
   * Front Door `/api/portals-api` path (e.g. https://members.dev.mdvip.com/api/portals-api), so API
   * calls are same-origin. Must stay absolute: getEnvironment() parses it and native builds need a host.
   */
  MDVIP_API_BASE_URL: string;
  /**
   * labs-api base URL, no trailing slash. Normally the path `/api/awp`, resolved against the page's
   * own origin — Front Door's `/api/*` route when deployed, the Vite dev proxy locally — so one value
   * serves every portal, port and environment. An absolute URL also works (native builds, whose
   * origin is `capacitor://localhost`, will need one). Omit where nothing reads lab data.
   */
  LABS_API_BASE_URL?: string;
  /** Idle timeout in milliseconds before showing the session warning dialog. Defaults to 1800000 (30 min). */
  IDLE_TIMEOUT_MS?: number;
  /** Duration in milliseconds of the countdown warning before auto-logout. Defaults to 20000 (20 sec). */
  WARNING_DURATION_MS?: number;
  /** URL scheme for deep link SAML callbacks on native mobile (e.g., 'mdvip-members'). Required on native platforms. */
  MOBILE_URL_SCHEME?: string;
  /** Fraction of token lifetime consumed before a proactive refresh is triggered on tab/app focus (0–1). Defaults to 0.75. */
  TOKEN_REFRESH_THRESHOLD?: number;
  /** Application Insights connection string. Omit to disable browser telemetry (e.g., local dev). */
  APPLICATIONINSIGHTS_CONNECTION_STRING?: string;
  /** Backend request-attribution entity ID (e.g., 'mdvip_connect_web'). Varies per environment. */
  ENTITY_ID?: string;
}

export type Environment = 'dev' | 'support' | 'prod' | 'local';

/**
 * Get the GraphQL endpoint URL
 */
export function getGraphqlUrl(config: AppConfig): string {
  return `${config.MDVIP_API_BASE_URL}/graphql`;
}

/**
 * Get the health check URL
 */
export function getHealthUrl(config: AppConfig): string {
  return `${config.MDVIP_API_BASE_URL}/health`;
}

const PROD_HOSTS = new Set(['members.mdvip.com', 'providers.mdvip.com', 'api.mdvip.com']);

/**
 * Derive the environment from the API base URL hostname
 * - members.dev.mdvip.com, portals-api.dev.mdvip.com -> dev
 * - providers.support.mdvip.com -> support
 * - members.mdvip.com, providers.mdvip.com, api.mdvip.com -> prod
 * - localhost -> local
 */
export function getEnvironment(config: AppConfig): Environment {
  try {
    const hostname = new URL(config.MDVIP_API_BASE_URL).hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
    if (hostname.includes('.dev.')) return 'dev';
    if (hostname.includes('.support.')) return 'support';
    if (PROD_HOSTS.has(hostname)) return 'prod';
    return 'dev'; // fallback
  } catch {
    return 'local';
  }
}
